export const TIMELINE_SCALES = ['day', 'week', 'month'];
/** How wide one day is drawn at each scale, in pixels. */
export const DAY_WIDTH = { day: 36, week: 14, month: 4 };
/** The fewest days each scale shows, so a short project still fills a frame rather than a corner of it. */
const MIN_DAYS = { day: 21, week: 56, month: 182 };
const DAY_MS = 86_400_000;
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
/** A day as a count of days since 1970-01-01, so day arithmetic never meets a clock change. */
export function epochDay(day) {
    const m = ISO_DAY.exec(day);
    if (!m)
        return NaN;
    return Math.round(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS);
}
export function dayAt(epoch) {
    return new Date(epoch * DAY_MS).toISOString().slice(0, 10);
}
/**
 * The calendar day a value falls on: a `YYYY-MM-DD` as written, a moment on the day it has in the
 * workspace's zone, anything else none — a bar is never placed on a day guessed from words.
 */
export function dayOf(value, timeZone) {
    if (value === null || value === undefined || value === '')
        return null;
    if (typeof value === 'string' && ISO_DAY.test(value))
        return value;
    if (typeof value !== 'string' && !(value instanceof Date))
        return null;
    const at = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(at.getTime()))
        return null;
    try {
        return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);
    }
    catch {
        return at.toISOString().slice(0, 10);
    }
}
/** The day a week starts on: the first day that is not a weekend day, after one that is. Sunday after Friday + Saturday. */
export function weekStart(weekend) {
    for (let d = 0; d < 7; d++)
        if (weekend.includes((d + 6) % 7) && !weekend.includes(d))
            return d;
    return 0;
}
const weekday = (epoch) => (epoch + 4) % 7; // 1970-01-01 was a Thursday
function truthy(value) {
    return value === true || value === 'true' || value === 1 || value === '1';
}
/**
 * The tone a row's value takes from its column's `tones` — one vocabulary with the badge (V-2). A
 * boolean column (a critical path) maps "true".
 */
export function toneOf(column, value) {
    if (!column?.tones || value === null || value === undefined || value === '')
        return null;
    const key = column.type === 'boolean' ? (truthy(value) ? 'true' : 'false') : String(value);
    return column.tones[key] ?? null;
}
/** A figure 0–100, or null: a progress of words fills nothing. */
export function progressOf(value) {
    if (value === null || value === undefined || value === '' || typeof value === 'boolean')
        return null;
    const n = typeof value === 'number' ? value : Number(String(value).replace(/[%\s]/g, ''));
    if (!Number.isFinite(n))
        return null;
    return Math.min(100, Math.max(0, n));
}
function fmt(locale, options) {
    let f;
    try {
        f = new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' });
    }
    catch {
        f = new Intl.DateTimeFormat('en', { ...options, timeZone: 'UTC' });
    }
    return (epoch) => f.format(new Date(epoch * DAY_MS));
}
/** A day as the reader says it, Western digits: «22 أكتوبر 2026», «22 Oct 2026». */
export function dayWords(day, locale) {
    const e = epochDay(day);
    return Number.isNaN(e) ? day : fmt(locale, { day: 'numeric', month: 'short', year: 'numeric' })(e);
}
/** Today in a time zone, `YYYY-MM-DD`. */
export function todayIn(timeZone) {
    return dayOf(new Date(), timeZone) ?? new Date().toISOString().slice(0, 10);
}
/**
 * The rows laid out on the axis. `rows` are what the view left — every row the filters keep on a
 * complete or windowed source; on a `query` source, the page in view, which the timeline names.
 */
export function timelineOf(manifest, rows, options) {
    const spec = manifest.timeline;
    const columns = new Map((manifest.columns ?? []).map((c) => [c.key, c]));
    const { scale } = options;
    const weekend = options.weekend ?? [5, 6];
    const today = options.today ?? todayIn(options.timeZone);
    const dayWidth = DAY_WIDTH[scale];
    const placed = [];
    let undated = 0;
    for (const row of rows) {
        const s = dayOf(row[spec.start], options.timeZone);
        if (!s) {
            undated++;
            continue;
        }
        const start = epochDay(s);
        const e = spec.end ? dayOf(row[spec.end], options.timeZone) : null;
        const forced = spec.milestone ? truthy(row[spec.milestone]) : false;
        const milestone = forced || !e;
        // An end before its start is the data's mistake: the bar is drawn one day long, never backwards.
        const end = milestone ? start : Math.max(start, epochDay(e));
        placed.push({ row, start, end, milestone });
    }
    if (!placed.length) {
        return { from: null, to: null, days: 0, dayWidth, groups: [], grouped: Boolean(spec.group), undated, weekend: [], today: null, major: [], minor: [] };
    }
    // The axis: every row, opened out to whole weeks or months at those scales, and to the fewest days
    // a scale fills a frame with.
    let first = Math.min(...placed.map((p) => p.start));
    let last = Math.max(...placed.map((p) => p.end));
    if (scale === 'day') {
        first -= 1;
        last += 1;
    }
    if (scale === 'week') {
        const ws = weekStart(weekend);
        first -= (weekday(first) - ws + 7) % 7;
        last += 6 - ((weekday(last) - ws + 7) % 7);
    }
    if (scale === 'month') {
        const a = new Date(first * DAY_MS);
        first = Math.round(Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), 1) / DAY_MS);
        const b = new Date(last * DAY_MS);
        last = Math.round(Date.UTC(b.getUTCFullYear(), b.getUTCMonth() + 1, 0) / DAY_MS);
    }
    if (last - first + 1 < MIN_DAYS[scale])
        last = first + MIN_DAYS[scale] - 1;
    const days = last - first + 1;
    // Swim-lanes in the order their first row arrives; the rows keep the view's order inside each.
    const groups = [];
    const byKey = new Map();
    const toneColumn = spec.tone ? columns.get(spec.tone) : undefined;
    for (const p of placed) {
        const gv = spec.group ? p.row[spec.group] : undefined;
        const gk = gv === null || gv === undefined ? '' : String(gv);
        let g = byKey.get(gk);
        if (!g) {
            g = { key: gk, first: spec.group && gk !== '' ? p.row : null, bars: [] };
            byKey.set(gk, g);
            groups.push(g);
        }
        g.bars.push({
            key: rowKeyOf(p.row, g.bars.length),
            row: p.row,
            start: p.start - first,
            span: p.end - p.start + 1,
            milestone: p.milestone,
            tone: spec.tone ? toneOf(toneColumn, p.row[spec.tone]) : null,
            progress: spec.progress && !p.milestone ? progressOf(p.row[spec.progress]) : null,
            from: dayAt(p.start),
            to: dayAt(p.end),
        });
    }
    // Rows with no group go last, under their own heading, rather than first because they arrived first.
    const none = byKey.get('');
    if (spec.group && none && groups.length > 1) {
        groups.splice(groups.indexOf(none), 1);
        groups.push(none);
    }
    const weekendDays = [];
    for (let i = 0; i < days; i++)
        if (weekend.includes(weekday(first + i)))
            weekendDays.push(i);
    const t = epochDay(today);
    const todayAt = !Number.isNaN(t) && t >= first && t <= last ? t - first : null;
    return { from: dayAt(first), to: dayAt(last), days, dayWidth, groups, grouped: Boolean(spec.group), undated, weekend: weekendDays, today: todayAt, ...ticksOf(first, last, scale, weekend, options.locale) };
}
function rowKeyOf(row, i) {
    const id = row['id'] ?? row['key'];
    if (id !== undefined && id !== null)
        return String(id);
    try {
        return JSON.stringify(row);
    }
    catch {
        return String(i);
    }
}
/** Runs of days sharing a label: each month, each year. */
function runs(first, last, keyOf, label) {
    const out = [];
    for (let e = first; e <= last; e++) {
        const prev = out[out.length - 1];
        if (prev && keyOf(first + prev.offset) === keyOf(e))
            prev.span++;
        else
            out.push({ offset: e - first, span: 1, label: label(e), day: dayAt(e) });
    }
    return out;
}
function ticksOf(first, last, scale, weekend, locale) {
    const month = (e) => dayAt(e).slice(0, 7);
    const year = (e) => dayAt(e).slice(0, 4);
    const monthYear = fmt(locale, { month: 'long', year: 'numeric' });
    if (scale === 'month') {
        return {
            major: runs(first, last, year, fmt(locale, { year: 'numeric' })),
            minor: runs(first, last, month, fmt(locale, { month: 'short' })),
        };
    }
    const major = runs(first, last, month, monthYear);
    if (scale === 'day') {
        const d = fmt(locale, { day: 'numeric' });
        const minor = [];
        for (let e = first; e <= last; e++)
            minor.push({ offset: e - first, span: 1, label: d(e), day: dayAt(e) });
        return { major, minor };
    }
    const ws = weekStart(weekend);
    const d = fmt(locale, { day: 'numeric', month: 'short' });
    const minor = [];
    for (let e = first; e <= last; e++) {
        if (weekday(e) === ws || e === first) {
            const prev = minor[minor.length - 1];
            if (prev)
                prev.span = e - first - prev.offset;
            minor.push({ offset: e - first, span: 1, label: d(e), day: dayAt(e) });
        }
    }
    const tail = minor[minor.length - 1];
    if (tail)
        tail.span = last - first + 1 - tail.offset;
    return { major, minor };
}
/** Which scales the viewer may choose between, the first being what opens. */
export function scalesOf(spec) {
    const declared = (spec.scales ?? []).filter((s) => TIMELINE_SCALES.includes(s));
    return declared.length ? declared : ['week', 'month', 'day'];
}
/**
 * What this version cannot draw faithfully in a timeline, before it is drawn — the browser's half
 * of the schema's `timelineProblems`, for a manifest that arrives as something else than was built.
 */
export function inspectTimeline(manifest) {
    const problems = [];
    const spec = manifest.timeline;
    if (!spec) {
        if (manifest.layout === 'timeline')
            problems.push({ code: 'MALFORMED', detail: 'a list laid out as a timeline declares no timeline block to draw it from' });
        return problems;
    }
    if (manifest.kind !== 'list') {
        problems.push({ code: 'MALFORMED', detail: 'a timeline lays out a list\'s rows; this screen is not a list' });
        return problems;
    }
    if (typeof spec !== 'object')
        return [{ code: 'MALFORMED', detail: 'the timeline is not a block of named columns' }];
    const columns = new Map((Array.isArray(manifest.columns) ? manifest.columns : []).filter(Boolean).map((c) => [c.key, c]));
    const part = (what, key, types) => {
        if (key === undefined)
            return;
        const column = typeof key === 'string' ? columns.get(key) : undefined;
        if (!column) {
            problems.push({ code: 'MALFORMED', detail: `${what} names "${String(key)}", which is not a declared column` });
            return;
        }
        const type = column.type ?? 'text';
        if (types && !types.includes(type))
            problems.push({ code: 'MALFORMED', detail: `${what} names "${String(key)}", whose type "${type}" is not ${types.join(' or ')}` });
    };
    if (spec.start === undefined)
        problems.push({ code: 'MALFORMED', detail: 'the timeline has no start, and a bar with no first day has nowhere to stand' });
    part('the timeline start', spec.start, ['date', 'datetime']);
    part('the timeline end', spec.end, ['date', 'datetime']);
    if (spec.label === undefined)
        problems.push({ code: 'MALFORMED', detail: 'the timeline has no label, and a bar with no name cannot be told from its neighbours' });
    part('the timeline label', spec.label);
    part('the timeline group', spec.group);
    part('the timeline progress', spec.progress, ['number', 'percent']);
    part('the timeline milestone', spec.milestone, ['boolean']);
    if (spec.tone !== undefined) {
        part('the timeline tone', spec.tone);
        const toned = columns.get(spec.tone);
        if (toned && (!toned.tones || !Object.keys(toned.tones).length))
            problems.push({ code: 'MALFORMED', detail: `the timeline tone names "${spec.tone}", which declares no tones` });
        else if (toned?.type === 'boolean' && toned.tones?.['true'] === undefined)
            problems.push({ code: 'MALFORMED', detail: `the timeline tone names the boolean "${spec.tone}", whose tones do not say what "true" is` });
    }
    for (const s of spec.scales ?? []) {
        if (!TIMELINE_SCALES.includes(s))
            problems.push({ code: 'MALFORMED', detail: `the timeline offers the scale "${String(s)}", which this version does not draw` });
    }
    // On a `query` source the page in view is drawn, named as that page (`Timeline`'s scope line).
    if (!manifest.source?.complete && !manifest.source?.window && !manifest.source?.query) {
        problems.push({ code: 'INCOMPLETE_SOURCE', detail: `a timeline draws every row in the days it shows, or a paged route's page named as one, and "${manifest.source?.path ?? 'this source'}" has declared none of whole, a window or a query` });
    }
    return problems;
}
//# sourceMappingURL=timeline-model.js.map